import { useCallback, useLayoutEffect, useState } from 'react';

/**
 * Resolved fixed-position coordinates for an anchored floating panel
 * (dropdown / popover / picker / menu).
 *
 * Exactly one of `top` / `bottom` is set: when `flip` is true the panel is
 * anchored by its bottom edge (opens upward), otherwise by its top edge
 * (opens downward). Spread the undefined one harmlessly into `style` — React
 * ignores `undefined` style values.
 */
export interface AnchoredPosition {
  top?: number;
  bottom?: number;
  left: number;
  /** Trigger width — handy for `w-full` panels that must match the trigger. */
  width: number;
  /** True when the panel was flipped to open upward (not enough room below). */
  flip: boolean;
}

export interface AnchoredPositionOptions {
  /** Approx panel height; used to decide whether to flip. Default 320. */
  estimatedHeight?: number;
  /** Approx panel width; used to clamp horizontally. Default = trigger width. */
  estimatedWidth?: number;
  /** Gap in px between the trigger and the panel. Default 6. */
  gap?: number;
  /**
   * Horizontal anchoring:
   * - `left` (default): panel left edge aligns to the trigger left edge.
   * - `right`: panel right edge aligns to the trigger right edge.
   */
  align?: 'left' | 'right';
  /** Viewport margin kept when clamping. Default 8. */
  margin?: number;
  /**
   * Optional custom measurement (e.g. measure an inner box instead of the
   * trigger element). MUST be memoised by the caller (`useCallback`) or it
   * re-subscribes the resize/scroll listeners every render.
   */
  measure?: (el: HTMLElement) => DOMRect;
}

/**
 * Shared anchored-positioning hook for portalled floating panels.
 *
 * Computes a `position: fixed` placement that:
 *  - opens downward by default, but **flips upward** when there isn't enough
 *    room below the trigger and there's more room above (the bug behind
 *    selectors/pickers/menus getting clipped at the bottom of the viewport);
 *  - clamps horizontally so the panel never overflows the viewport edges.
 *
 * Recomputes on open and on window resize / scroll (capture phase, so it
 * tracks scrolling ancestors too). Returns `null` while closed.
 *
 * Mirrors the inline logic already proven in `DropdownSelect` so every
 * anchored panel shares one behaviour. See Standards.md §"Anchored panels".
 */
export function useAnchoredPosition(
  triggerRef: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
  options: AnchoredPositionOptions = {},
): AnchoredPosition | null {
  const {
    estimatedHeight = 320,
    estimatedWidth,
    gap = 6,
    align = 'left',
    margin = 8,
    measure,
  } = options;
  const [pos, setPos] = useState<AnchoredPosition | null>(null);

  const compute = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = measure ? measure(el) : el.getBoundingClientRect();
    const width = estimatedWidth ?? r.width;
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    // Flip up only when below is cramped AND there's genuinely more room above
    // (never flip into an even tighter space).
    const flip = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
    const desiredLeft = align === 'right' ? r.right - width : r.left;
    const maxLeft = Math.max(margin, window.innerWidth - width - margin);
    const left = Math.min(Math.max(desiredLeft, margin), maxLeft);
    setPos({
      top: flip ? undefined : r.bottom + gap,
      bottom: flip ? window.innerHeight - r.top + gap : undefined,
      left,
      width: r.width,
      flip,
    });
  }, [triggerRef, estimatedHeight, estimatedWidth, gap, align, margin, measure]);

  useLayoutEffect(() => {
    if (!isOpen) {
      // Clear so a reopen never paints one frame at stale coordinates.
      setPos(null);
      return;
    }
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [isOpen, compute]);

  return pos;
}
